<template>
  <main class="main" :class="classes" v-show="!isLoading">
    <div
      class="sales-funnel-wrapper"
      :class="{
        'has-warning': hasWarning,
        'has-success': hasSuccess,
        'has-notice': hasNotice,
        'has-bottom-space': bottomSpace,
        'is-widget': isWidget,
      }"
    >
      <div class="sales-funnel-wrapper__container">
        <header v-if="hasHeaderSlot" class="sales-funnel-wrapper__header">
          <slot name="header" />
        </header>
        <div v-if="hasStatusSlot" class="sales-funnel-wrapper__status">
          <slot name="status" />
        </div>
        <div class="sales-funnel-wrapper__body">
          <slot name="body" />
        </div>
        <div v-if="hasBodyWideSlot" class="sales-funnel-wrapper__body-wide">
          <slot name="bodyWide" />
        </div>
        <footer v-if="hasFooterSlot" class="sales-funnel-wrapper__footer">
          <div
            v-if="hasSubfooterSlot"
            class="sales-funnel-wrapper__subfooter"
          >
            <slot name="subfooter" />
          </div>
          <div
            class="sales-funnel-wrapper__footer-main"
            :class="{ 'sales-funnel-wrapper__footer-main--round': showAuthFlowProgressBar }"
          >
            <slot name="footer" />
          </div>
        </footer>
      </div>
      <div class="sales-funnel-wrapper__postfix">
        <p
          v-if="hasComplianceMessage"
          class="sales-funnel-wrapper__postfix-compliance"
        >
          <slot name="complianceMessage" />
        </p>
        {{ $t('transaction-flow.shared.secured') }}
      </div>
      <div v-if="hasInfoboxSlot" class="sales-funnel-wrapper__infobox">
        <slot name="infobox" />
      </div>
    </div>
    <slot name="modal" />
  </main>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'Wrapper',

  props: {
    hasWarning: { type: Boolean, required: false, default: false },
    hasSuccess: { type: Boolean, required: false, default: false },
    hasNotice: { type: Boolean, required: false, default: false },
    bottomSpace: { type: Boolean, required: false, default: false },
    isLoading: { type: Boolean, required: false, default: false },
    showAuthFlowProgressBar: { type: Boolean, required: false, default: false },
  },

  computed: {
    ...mapGetters('settings', ['isWidget']),

    classes() {
      return {
        'main--auth': this.showAuthFlowProgressBar,
        'main--private': !this.showAuthFlowProgressBar,
      };
    },

    hasComplianceMessage() {
      return !!this.$slots.complianceMessage;
    },

    hasHeaderSlot() {
      return !!this.$slots.header;
    },

    hasStatusSlot() {
      return !!this.$slots.status;
    },

    hasBodySlot() {
      return !!this.$slots.body;
    },

    hasBodyWideSlot() {
      return !!this.$slots.bodyWide;
    },

    hasFooterSlot() {
      return !!this.$slots.footer || !!this.$slots.subfooter;
    },

    hasSubfooterSlot() {
      return !!this.$slots.subfooter;
    },

    hasInfoboxSlot() {
      return !!this.$slots.infobox;
    },
  },
};
</script>

<style scoped lang="scss">
@use 'scss-vars';

.sales-funnel-wrapper {
  position: relative;
  width: 100%;
  padding: 16px 16px 32px;

  &.is-widget {
    max-width: 100%;
  }

  &__container {
    background-color: scss-vars.$white;
    border-radius: 10px;
    width: 672px;
    max-width: 100%;
    margin: 0 auto;
  }

  &__header {
    border-radius: 10px 10px 0 0;
  }

  &.has-warning &__header,
  &.has-warning &__status {
    background-color: rgba(scss-vars.$red, 0.1);
  }

  &.has-success &__header,
  &.has-success &__status {
    background-color: rgba(scss-vars.$green, 0.1);
  }

  &.has-notice &__header,
  &.has-notice &__status {
    background-color: scss-vars.$grey-04;
  }

  &__status {
    padding: 0 16px 10px 16px;
    font-family: scss-vars.$font-bold;
    font-size: 14px;
    line-height: 1.29;
    text-align: center;
  }

  &.has-warning &__status {
    color: scss-vars.$red;
  }

  &.has-success &__status {
    color: scss-vars.$green;
  }

  &__body {
    width: 448px;
    max-width: 100%;
    margin: 0 auto;
    padding: 24px 16px;

    :deep(.auth-app__inner) {
      padding: 0;
    }

    :deep(.auth-app__body) {
      padding: 0;
    }
  }

  // XXX: Auth flow with progress bar intentionally uses edge-to-edge content — no horizontal padding
  .main--auth &__body {
    padding: 24px 0;
  }

  &.has-bottom-space &__body {
    padding-bottom: 40px;
  }

  &__footer-main {
    background-color: scss-vars.$grey-04;
    border-radius: 0 0 10px 10px;

    &--round {
      background-color: scss-vars.$violet-15;
      border-radius: 8px;
    }
  }

  &__subfooter {
    padding-bottom: 16px;
  }

  &__postfix {
    text-align: center;
    margin-top: 16px;
    font-size: 14px;
    line-height: 1.14;
  }

  &__postfix-compliance {
    max-width: 672px;
    padding-bottom: 8px;
    margin: 0 auto;
  }

  &__infobox {
    width: 672px;
    max-width: 100%;
    background-color: scss-vars.$black-20;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.29;
    color: scss-vars.$white;
  }

  &__infobox:before {
    content: '\e920';
    @include scss-vars.inline-icon;

    position: absolute;
    left: 16px;
    top: 17px;
    font-size: 16px;
  }

  [dir='rtl'] &__infobox:before {
    right: 16px;
    left: unset;
  }

  @media screen and (max-width: scss-vars.$screen-md-max) {
    &__infobox {
      position: relative;
      margin: 16px auto 0 auto;
      padding: 16px 16px 16px 44px;
    }

    [dir='rtl'] &__infobox {
      padding: 16px 44px 16px 16px;
    }
  }

  @media screen and (min-width: scss-vars.$screen-lg-min) {
    width: scss-vars.cols('lg', 12);
    margin: 0 auto;
    padding: 64px 0 80px 0;

    &__status {
      padding: 10px 16px;
      font-family: scss-vars.$font-bold;
      font-size: 14px;
      line-height: 1.29;
      text-align: center;
    }

    &__body {
      max-width: 416px;
      margin: 0 auto;
      padding: 32px 0;
    }

    &.has-bottom-space &__body {
      padding-bottom: 64px;
    }

    &__body-wide {
      padding: 36px 0 24px 0;
    }

    &__postfix {
      margin-top: 24px;
    }

    &__infobox {
      position: absolute;
      right: 0;
      top: 64px;
      width: scss-vars.cols('lg', 2);
      max-width: 160px;
      padding: 48px 16px 32px 16px;
    }

    [dir='rtl'] &__infobox {
      left: 0;
      right: unset;
    }
  }

  @media screen and (min-width: scss-vars.$screen-xlg-min) {
    width: scss-vars.cols('xlg', 12);

    &__infobox {
      right: 0;
    }
  }
}
</style>
